import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { InquisitiveLanguageCodes } from 'global/graphql/scalars.type';
import { Dropdown } from 'approot/shared/dropdown/dropdown';
import { DropdownDivider } from 'approot/shared/dropdown/dropdown-item.style';
import { DropdownItemDescriptive } from 'approot/shared/dropdown/dropdown-item';
import {
  BASE_LANGUAGE_OPTIONS,
  OTHER_LANGUAGE_OPTIONS,
} from '../lang/activity-language.constants';
import { getLanguageLabel } from '../logic/get-language-label';
import {
  ActivitiesContentType,
  LANGUAGE_CODE_EN_AU,
  LanguageCodes,
} from 'global/constants';
import useTranslateActivities from '../lang/use-translate-activities';
import { DownloadingModal } from 'approot/shared/downloading-modal/downloading-modal';
import { studentAuthApolloContextOption } from 'approot/student-class-to-do/student-class-to-do.utils';

type Props = {
  language: InquisitiveLanguageCodes;
  availableTranslations: InquisitiveLanguageCodes[];
  version?: string;
  contentType: ActivitiesContentType;
  isStudent?: boolean;
};

const ActivityPresentLanguage = ({
  language,
  availableTranslations,
  version,
  contentType,
  isStudent = false,
}: Props) => {
  const [selectedLanguage, setSelectedLanguage] =
    useState<LanguageCodes>(language);
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    translate,
    loading: translateLoading,
    error,
  } = useTranslateActivities();

  const handleChangeLanguage = (selectedLanguage: InquisitiveLanguageCodes) => {
    if (selectedLanguage !== language) {
      if (selectedLanguage === LANGUAGE_CODE_EN_AU) {
        searchParams.delete('lang');
        setSearchParams(searchParams);
        window.location.reload();
        return;
      }

      if (!availableTranslations.includes(selectedLanguage) && version) {
        setSelectedLanguage(selectedLanguage);
        // trigger a translation event
        translate({
          ...(isStudent ? { context: studentAuthApolloContextOption() } : {}),
          variables: {
            input: {
              contentType,
              version,
              languages: [selectedLanguage],
            },
          },
        }).then(() => {
          searchParams.set('lang', selectedLanguage);
          setSearchParams(searchParams);
          window.location.reload();
        });
      } else {
        searchParams.set('lang', selectedLanguage);
        setSearchParams(searchParams);
        window.location.reload();
      }
    }
  };

  return (
    <>
      {translateLoading && (
        <DownloadingModal
          isLoading
          title="Preparing your Activities"
          text={`Working our magic in ${getLanguageLabel(selectedLanguage)}`}
          hasError={!!error}
          errorMessage="Unable to show the selected language. Please try again"
        />
      )}

      <Dropdown
        aria-label={`Change Language of Activities. Current Language: ${getLanguageLabel(
          language
        )}`}
        menuMatchToggleWidth
        selectedValue={getLanguageLabel(language)}
        dropdownContents={
          <>
            {BASE_LANGUAGE_OPTIONS.map(option => (
              <DropdownItemDescriptive
                key={option.value}
                primaryText={option.label}
                secondaryText={option.labelSecondary}
                isActive={option.value === language}
                onClick={() => handleChangeLanguage(option.value)}
              />
            ))}

            <DropdownDivider />

            {OTHER_LANGUAGE_OPTIONS.map(option => (
              <DropdownItemDescriptive
                key={option.value}
                primaryText={option.label}
                secondaryText={option.labelSecondary}
                isActive={option.value === language}
                onClick={() => handleChangeLanguage(option.value)}
              />
            ))}
          </>
        }
      />
    </>
  );
};

export default ActivityPresentLanguage;
